import type { ReactElement } from "react";
import type { Booking, Customer360, CustomerActivity, CustomerSummary, NotificationPreferences } from "@kuroo/contracts";
import { KurooApiError } from "@kuroo/sdk";

export type CustomerRecord = Customer360 & { bookings: Booking[] };
export type CustomerTab = "overview" | "activity" | "financial";

export const activityLabels: Record<CustomerActivity["kind"], string> = {
  note: "Nota",
  task: "Tarea",
  communication: "Comunicación",
  system: "Sistema"
};

export const activityIcons: Record<CustomerActivity["kind"], string> = {
  note: "✎",
  task: "✓",
  communication: "◌",
  system: "↗"
};

export const preferenceLabels: Array<[keyof Omit<NotificationPreferences, "userId" | "updatedAt">, string, string]> = [
  ["emailEnabled", "Email transaccional", "Confirmaciones, recibos y cambios de reserva."],
  ["smsEnabled", "SMS", "Avisos urgentes y recordatorios operativos."],
  ["whatsappEnabled", "WhatsApp", "Mensajes de atención y seguimiento."],
  ["chatwootEnabled", "Chat", "Conversaciones gestionadas por el equipo."],
  ["remindersEnabled", "Recordatorios", "Anticipos configurados para sus reservas."],
  ["marketingEnabled", "Marketing", "Novedades y comunicaciones promocionales."]
];

export function readableError(reason: unknown): string {
  return reason instanceof Error ? reason.message : "La operación no está disponible temporalmente.";
}

export function isUnauthenticated(reason: unknown): boolean {
  return reason instanceof KurooApiError && reason.code === "UNAUTHENTICATED";
}

export function customerName(customer: Pick<CustomerSummary, "firstName" | "lastName" | "email">): string {
  const name = [customer.firstName, customer.lastName].filter((part) => part.trim()).join(" ").trim();
  return name || customer.email;
}

export function customerInitials(customer: Pick<CustomerSummary, "firstName" | "lastName" | "email">): string {
  const parts = [customer.firstName, customer.lastName].map((part) => part.trim()).filter(Boolean);
  return parts.length ? parts.slice(0, 2).map((part) => part[0]).join("").toUpperCase() : customer.email.slice(0, 2).toUpperCase();
}

export function formatCustomerDate(value: string | null | undefined, timezone = "Europe/Madrid"): string {
  if (!value) return "Sin fecha";
  try {
    return new Intl.DateTimeFormat("es-ES", { dateStyle: "medium", timeStyle: "short", timeZone: timezone }).format(new Date(value));
  } catch {
    return value;
  }
}

export function formatCustomerMoney(cents: number, currency = "EUR"): string {
  try {
    return new Intl.NumberFormat("es-ES", { style: "currency", currency }).format(cents / 100);
  } catch {
    return (cents / 100).toFixed(2) + " " + currency;
  }
}

export function bookingStatusLabel(status: Booking["status"]): string {
  return ({ confirmed: "Confirmada", pending: "Pendiente", cancelled: "Cancelada", rejected: "Rechazada", no_show: "No asistió" } as Record<Booking["status"], string>)[status] ?? status;
}

export function activityStatusLabel(status: CustomerActivity["status"]): string {
  return status === "completed" ? "Completada" : "Abierta";
}

export function paymentStatusLabel(status: string): string {
  return ({ paid: "Pagado", authorized: "Autorizado", pending: "Pendiente", failed: "Fallido", refunded: "Reembolsado", partially_refunded: "Parcialmente reembolsado", cancelled: "Cancelado" } as Record<string, string>)[status] ?? status;
}

export function communicationStatusLabel(status: string): string {
  return ({ sent: "Enviado", queued: "En cola", processing: "Procesando", failed: "Fallido", suppressed: "Suprimido" } as Record<string, string>)[status] ?? status;
}

export function inputDateTime(value: string): string | null {
  if (!value.trim()) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

export function customerProfileTags(value: string): string[] {
  return Array.from(new Set(value.split(",").map((tag) => tag.trim().toLowerCase()).filter(Boolean))).slice(0, 50);
}

export function EmptyCustomerPanel({ children }: { children: string }): ReactElement {
  return <div className="customer-360-empty"><span className="customer-360-empty-mark" aria-hidden="true">◎</span><strong>Seleccioná un cliente</strong><p>{children}</p></div>;
}

export function CustomerAvatar({ customer, large = false }: { customer: Pick<CustomerSummary, "firstName" | "lastName" | "email">; large?: boolean }): ReactElement {
  return <span className={"customer-360-avatar" + (large ? " large" : "")} aria-hidden="true">{customerInitials(customer)}</span>;
}
